/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useState, type FunctionComponent } from 'react';
import { ADDRESS_LAYOUT_TYPES } from '../Address';
import RemoveAddressModal from '../RemoveAddressModal';
import * as styles from './AddressList.module.scss';

interface Address {
  id: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  isDefault?: boolean;
}

interface AddressListProps {
  addresses: { userAddress: Address }[];
  selectedAddressId?: string;
  setSelectedAddressId?: (id: string) => void;
  layoutType?: string;
  handleEditAddress: (address: Address) => void;
  handleRemoveAddress: (id: string) => Promise<void>;
}

const getAddressFormatted = (address: Address) => (
  <>
    <strong>
      {address.firstName} {address.lastName}
    </strong>{' '}
    {address.addressLine1}
    {address.addressLine2 ? `, ${address.addressLine2}` : ','} {address.city}, {address.state}{' '}
    {address.zipcode}, {address.country}
  </>
);

const AddressList: FunctionComponent<AddressListProps> = ({
  addresses,
  selectedAddressId,
  setSelectedAddressId,
  handleEditAddress,
  handleRemoveAddress,
  layoutType,
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [currentAddressToRemove, setCurrentAddressToRemove] = useState<Address | null>(null);
  const [sortedAddresses, setSortedAddresses] = useState<{ userAddress: Address }[]>([]);
  const isUserAccountLayout = layoutType === ADDRESS_LAYOUT_TYPES.userAccount;

  const openModal = (address: Address) => {
    setCurrentAddressToRemove(address);
    setIsRemoveModalOpen(true);
  };
  const closeModal = () => setIsRemoveModalOpen(false);
  const removeAddress = () => {
    if (currentAddressToRemove) {
      handleRemoveAddress(currentAddressToRemove.id).then(() => closeModal());
    }
  };

  useEffect(() => {
    const sorted = [...addresses].sort((a, b) => {
      if (a.userAddress?.isDefault) return -1;
      if (b.userAddress?.isDefault) return 1;
      return 0;
    });
    setSortedAddresses(sorted);
  }, [addresses]);

  return (
    <>
      <div className={styles.rows} data-testid="address-list" data-e2e="address-list">
        {isUserAccountLayout && (
          <div className={styles.rowAccount}>
            <h3>Primary Address</h3>
          </div>
        )}
        {sortedAddresses.map(({ userAddress }) => (
          <div className={styles.row} key={userAddress.id}>
            <div className={styles.address}>
              {!isUserAccountLayout && (
                <input
                  type="radio"
                  name="address"
                  value={userAddress.id}
                  checked={selectedAddressId === userAddress.id}
                  onChange={() => setSelectedAddressId && setSelectedAddressId(userAddress.id)}
                />
              )}
              <div>
                <p>{getAddressFormatted(userAddress)}</p>
                {/* TODO: Please fix the a11y issues with this element and then remove the comments at the top of the file. */}
                <span onClick={() => handleEditAddress(userAddress)}>Edit address</span>
                {!userAddress?.isDefault && (
                  <span className={styles.removeAddress} onClick={() => openModal(userAddress)}>
                    Remove address
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {currentAddressToRemove && (
        <RemoveAddressModal
          isOpen={isRemoveModalOpen}
          onCancel={closeModal}
          onRemove={removeAddress}
          {...(currentAddressToRemove || {})}
        />
      )}
    </>
  );
};

export default AddressList;
